import {
  ArrowNarrowRight,
  Check,
  Lock01,
  LockUnlocked01,
} from "@untitledui/icons"
import { motion } from "motion/react"

import { Badge } from "@/components/base/badges/badges"
import type { SessionDebriefPayload } from "@/types/conversation"

// The hero payoff: a scripted "What we saw" card. It closes the loop the coach
// opened at goal-selection ("your job is to open doors"), made literal — the
// four GP-held disclosures, opened vs left; the trainee's own softening lines
// named in plain English; and the journey through four communication stages.
// Composed to match the surrounding ConversationView cards (bg-white, rounded-xl,
// shadow-md) rather than the ui/card token surface.

export default function DebriefSurface({
  debrief,
}: {
  debrief: SessionDebriefPayload
}) {
  if (!debrief) return null

  const {
    headline,
    doors_opened,
    doors_total,
    doors,
    softening,
    pace_journey,
  } = debrief

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="space-y-8 rounded-xl bg-white p-8 shadow-md dark:bg-gray-900"
    >
      {/* Header */}
      <div className="space-y-2">
        <Badge type="pill-color" color="warning" size="md">
          Session debrief
        </Badge>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {headline ?? "What we saw"}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Here&apos;s how the conversation went — and where doors opened to
          getting help.
        </p>
      </div>

      {/* 1. Disclosure funnel — the visual centrepiece */}
      <div className="space-y-4">
        <div className="flex items-end gap-4">
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.35, ease: "easeOut" }}
            className="flex items-baseline"
          >
            <span className="text-5xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
              {doors_opened}
            </span>
            <span className="text-2xl font-semibold text-gray-400 dark:text-gray-500">
              /{doors_total}
            </span>
          </motion.div>
          <div className="pb-1">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              doors opened
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              the help a GP can offer — when you give them a way in
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {doors.map((door) => {
            const heavy = door.weight >= 5
            return door.opened ? (
              <div
                key={door.id}
                className={`flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-800/60 dark:bg-green-900/20 ${
                  heavy ? "ring-1 ring-green-200 dark:ring-green-800/60" : ""
                }`}
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700 dark:bg-green-800/40 dark:text-green-300">
                  <LockUnlocked01 className="size-4" />
                </span>
                <span className="flex-1 text-sm font-medium text-green-900 dark:text-green-200">
                  {door.label}
                </span>
                <Check className="size-4 shrink-0 text-green-600 dark:text-green-400" />
              </div>
            ) : (
              <div
                key={door.id}
                className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 opacity-80 dark:border-gray-700 dark:bg-gray-800/40"
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-gray-700/60 dark:text-gray-500">
                  <Lock01 className="size-4" />
                </span>
                <span className="flex-1 text-sm font-medium text-gray-500 dark:text-gray-400">
                  {door.label}
                </span>
                <span className="shrink-0 rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                  {door.left_note ?? "left on the table"}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* 2. Where you softened yourself — the real quotes, named in plain English */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold tracking-wide text-gray-900 uppercase dark:text-gray-100">
          Where you softened yourself
        </h3>
        <div className="space-y-4">
          {softening.map((s, i) => (
            <div
              key={i}
              className="rounded-lg border border-amber-100 bg-amber-50/60 p-4 dark:border-amber-900/40 dark:bg-amber-900/10"
            >
              <blockquote className="border-l-2 border-amber-300 pl-3 dark:border-amber-700">
                <p className="text-sm leading-relaxed text-gray-800 italic dark:text-gray-200">
                  “{s.quote}”
                </p>
              </blockquote>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Badge type="pill-color" color="warning" size="sm">
                  {s.label}
                </Badge>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {s.gloss}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 3. PACE journey strip — the name "PACE" introduced once, late, as credibility */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold tracking-wide text-gray-900 uppercase dark:text-gray-100">
          Your journey
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          We tracked you through four communication stages — what clinicians
          call{" "}
          <span className="font-semibold text-gray-700 dark:text-gray-300">
            PACE
          </span>
          .
        </p>
        <div className="flex flex-wrap items-center gap-x-1 gap-y-2">
          {pace_journey.map((stage, i) => (
            <div key={stage.id} className="flex items-center gap-1">
              <span
                className={`rounded-full px-3 py-1.5 text-xs font-medium ${
                  stage.reached
                    ? "bg-brand-50 text-brand-700 ring-brand-200 dark:bg-brand-900/20 dark:text-brand-300 dark:ring-brand-800/60 ring-1"
                    : "bg-gray-50 text-gray-400 ring-1 ring-gray-200 dark:bg-gray-800/40 dark:text-gray-500 dark:ring-gray-700"
                }`}
              >
                {stage.label}
              </span>
              {i < pace_journey.length - 1 && (
                <ArrowNarrowRight className="size-4 text-gray-300 dark:text-gray-600" />
              )}
            </div>
          ))}
        </div>
      </div>
    </motion.section>
  )
}
